import type { EditorOptions } from "@tiptap/core";
import { Blockquote } from "@tiptap/extension-blockquote";
import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Code } from "@tiptap/extension-code";
import { CodeBlock } from "@tiptap/extension-code-block";
import { Color } from "@tiptap/extension-color";
import { Document } from "@tiptap/extension-document";
import { Dropcursor } from "@tiptap/extension-dropcursor";
import { FontFamily } from "@tiptap/extension-font-family";
import { Gapcursor } from "@tiptap/extension-gapcursor";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Highlight } from "@tiptap/extension-highlight";
import { History } from "@tiptap/extension-history";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { Italic } from "@tiptap/extension-italic";
import { Link } from "@tiptap/extension-link";
import { ListItem } from "@tiptap/extension-list-item";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Strike } from "@tiptap/extension-strike";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { Text } from "@tiptap/extension-text";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import OfficePaste from "@intevation/tiptap-extension-office-paste";

import { useMemo } from "react";

import {
  FontSize,
  HeadingWithAnchor,
  LinkBubbleMenuHandler,
  ResizableImage,
  TableImproved,
} from "mui-tiptap";

export type UseExtensionsOptions = {
  placeholder?: string;
};

const CustomLinkExtension = Link.extend({
  inclusive: false,
});

const CustomSubscript = Subscript.extend({
  excludes: "superscript",
});

const CustomSuperscript = Superscript.extend({
  excludes: "subscript",
});

export default function useExtensions({
  placeholder,
}: UseExtensionsOptions = {}): EditorOptions["extensions"] {
  return useMemo(() => {
    return [
      /**
       * TABLES
       */
      TableImproved.configure({
        resizable: true,
      }),

      TableRow,
      TableHeader,
      TableCell,

      /**
       * BASIC NODES
       */
      BulletList,
      CodeBlock,
      Document,
      HardBreak,
      ListItem,
      OrderedList,
      Paragraph,
      Text,

      /**
       * SUBSCRIPT / SUPERSCRIPT
       */
      CustomSubscript,
      CustomSuperscript,

      /**
       * MARKS
       */
      Bold,
      Blockquote,
      Code,
      Italic,
      Underline,
      Strike,

      /**
       * LINKS
       */
      CustomLinkExtension.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),

      LinkBubbleMenuHandler,

      /**
       * CURSORS
       */
      Gapcursor,

      /**
       * HEADINGS
       */
      HeadingWithAnchor,

      /**
       * ALIGNMENT
       */
      TextAlign.configure({
        types: ["heading", "paragraph", "image"],
      }),

      /**
       * STYLING
       */
      TextStyle,
      Color,
      FontFamily,
      FontSize,

      Highlight.configure({
        multicolor: true,
      }),

      HorizontalRule,

      /**
       * IMAGE (IMPORTANT)
       */
      ResizableImage.configure({
        inline: true,
        allowBase64: true,
      }),

      /**
       * DROP CURSOR
       */
      Dropcursor,

      /**
       * PLACEHOLDER
       */
      Placeholder.configure({
        placeholder,
      }),

      /**
       * HISTORY
       */
      History,

      /**
       * OFFICE PASTE
       */
      OfficePaste,
    ];
  }, [placeholder]);
}